import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import * as api from '../lib/api'
import HRDashboard from './HRDashboard'
import EmployeeDashboard from './EmployeeDashboard'
import ManagerDashboard from './ManagerDashboard'
import GlobalHRDashboard from './GlobalHRDashboard'
import { Spin, Alert, Typography, Space, Segmented } from 'antd'
import { SettingOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons'
import { useI18n } from '../i18n/I18nProvider'

const { Title } = Typography
const ACTIVE_VIEW_STORAGE_KEY = 'dashboard.activeView'

const readStoredView = () => {
  try {
    return window.localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY)
  } catch (e) {
    return null
  }
}

export default function Dashboard() {
  const { t } = useI18n()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeView, setActiveView] = useState(() => (
    searchParams.get('view') === 'manager'
      ? 'manager'
      : searchParams.has('tab')
        ? 'hr'
        : readStoredView()
  ))
  const [me, setMe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [appraisals, setAppraisals] = useState([])
  const [appraisalsLoading, setAppraisalsLoading] = useState(false)
  const [appraisalsLoaded, setAppraisalsLoaded] = useState(false)
  const [appraisalsError, setAppraisalsError] = useState(null)
  const appraisalsRequestRef = useRef(null)
  const defaultViewAppliedRef = useRef(false)

  useEffect(() => { api.getMe().then(setMe).catch(e => setError(e.message)).finally(() => setLoading(false)) }, [])

  const loadAppraisals = useCallback(async (force = false) => {
    if (!force && appraisalsLoaded) return appraisals
    if (!force && appraisalsRequestRef.current) return appraisalsRequestRef.current

    setAppraisalsLoading(true)
    setAppraisalsError(null)

    const request = api.getAppraisals({ session_status: 'active', role_scope: 1 })
      .then(rows => {
        setAppraisals(rows)
        setAppraisalsLoaded(true)
        return rows
      })
      .catch(e => {
        setAppraisalsError(e.message)
        throw e
      })
      .finally(() => {
        if (appraisalsRequestRef.current === request) appraisalsRequestRef.current = null
        setAppraisalsLoading(false)
      })

    appraisalsRequestRef.current = request
    return request
  }, [appraisals, appraisalsLoaded])

  const roles = me?.roles || []
  const isGlobalSuperadmin = roles.includes('frontend_superadmin') || roles.includes('global_hr_superadmin') || roles.includes('global_hr_tti_milwaukee_asia')
  const hasGlobalHRDashboard = roles.includes('global_hr') || isGlobalSuperadmin || me?.is_staff || me?.is_superuser
  const isHRSuperadmin = roles.includes('hr_superadmin') || isGlobalSuperadmin
  const isHRViewer = roles.includes('hr_viewer')
  const shouldRenderManagerDashboard = roles.some(role => ['manager', 'department_head', 'bu_head'].includes(role)) || me?.has_assigned_manager_appraisals
  const shouldPrioritizeManagerDashboard = roles.includes('hr_superadmin') && !hasGlobalHRDashboard && shouldRenderManagerDashboard
  const shouldRenderEmployeeDashboard = (roles.includes('employee') || me?.is_manager_with_direct_manager) && !shouldRenderManagerDashboard
  const shouldLoadRoleAppraisals = shouldRenderEmployeeDashboard || shouldRenderManagerDashboard
  useEffect(() => {
    if (shouldLoadRoleAppraisals) loadAppraisals().catch(() => { })
  }, [shouldLoadRoleAppraisals, loadAppraisals])

  useEffect(() => {
    if (!me || defaultViewAppliedRef.current) return
    defaultViewAppliedRef.current = true
    if (shouldPrioritizeManagerDashboard) setActiveView('manager')
  }, [me, shouldPrioritizeManagerDashboard])

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
  if (error) return <Alert type='error' message={error} />

  const appraisalDashboardProps = {
    me,
    appraisals,
    appraisalsLoading,
    appraisalsLoaded,
    appraisalsError,
    loadAppraisals
  }

  if (hasGlobalHRDashboard && !shouldRenderManagerDashboard) {
    return <GlobalHRDashboard me={me} />
  }

  // One entry per "hat" the user wears. When there is more than one, show them as
  // tabs instead of stacking every dashboard in one long page.
  const views = []
  const managerView = shouldRenderManagerDashboard
    ? {
        key: 'manager',
        label: t('dashboard.view.manager', 'My Team & Appraisals'),
        icon: <TeamOutlined />,
        children: <ManagerDashboard {...appraisalDashboardProps} />
      }
    : null
  if (shouldPrioritizeManagerDashboard) views.push(managerView)
  if (isHRSuperadmin || isHRViewer) {
    views.push({
      key: 'hr',
      label: t('dashboard.view.hr', 'HR Administration'),
      icon: <SettingOutlined />,
      children: hasGlobalHRDashboard
        ? <GlobalHRDashboard me={me} />
        : <HRDashboard {...appraisalDashboardProps} canManage={isHRSuperadmin} />
    })
  }
  if (managerView && !shouldPrioritizeManagerDashboard) views.push(managerView)
  if (shouldRenderEmployeeDashboard) {
    views.push({
      key: 'employee',
      label: t('dashboard.view.employee', 'My Appraisals'),
      icon: <UserOutlined />,
      children: <EmployeeDashboard {...appraisalDashboardProps} />
    })
  }

  const selectedView = views.some(view => view.key === activeView) ? activeView : views[0]?.key
  const selectedViewContent = views.find(view => view.key === selectedView)?.children
  const handleViewChange = key => {
    setActiveView(key)
    if (searchParams.has('view') || (key !== 'hr' && searchParams.has('tab'))) {
      const next = new URLSearchParams(searchParams)
      next.delete('view')
      if (key !== 'hr') next.delete('tab')
      setSearchParams(next, { replace: true })
    }
    try {
      window.localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, key)
    } catch (e) { /* storage unavailable; selection just won't persist */ }
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} className='tti-page-title' style={{ margin: 0 }}>{t('dashboard.page_title', 'Performance Appraisals')}</Title>
      </div>

      {roles.length === 0 && !me?.has_assigned_manager_appraisals && <Alert type='warning' message={t('dashboard.no_roles', 'No roles assigned to your account yet. Contact an administrator.')} showIcon style={{ marginBottom: 24 }} />}
      {appraisalsError && <Alert type='error' message={appraisalsError} showIcon style={{ marginBottom: 24 }} />}

      {views.length > 1
        ? (
          <>
            <div style={{ maxWidth: 520, marginBottom: 24 }}>
              <Segmented
                block
                className='tti-dashboard-view-switcher'
                size='large'
                value={selectedView}
                onChange={handleViewChange}
                options={views.map(view => ({
                  value: view.key,
                  label: view.label,
                  icon: view.icon
                }))}
              />
            </div>
            {selectedViewContent}
          </>
          )
        : (
          <Space direction='vertical' size='large' style={{ width: '100%' }}>
            {views.map(view => <div key={view.key}>{view.children}</div>)}
          </Space>
          )}
    </div>
  )
}
